/**
 * Hook for managing employee evaluations
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  EmployeeEvaluation,
  EmployeeEvaluationWithDetails,
  EvaluationFormData,
  EvaluationDetail,
  EvaluationDetailFormData,
  EvaluationFilters,
} from '@/types/evaluation';
import { useAuth } from '@/components/AuthProvider';

export const useEmployeeEvaluations = (filters?: EvaluationFilters) => {
  const { user } = useAuth();
  const [evaluations, setEvaluations] = useState<EmployeeEvaluation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch evaluations
  const fetchEvaluations = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let query = supabase
        .from('employee_evaluations')
        .select('*')
        .or(`evaluator_user_id.eq.${user.id},evaluator_user_id.is.null`);

      // Apply filters
      if (filters?.period_id) {
        query = query.eq('period_id', filters.period_id);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.from_date) {
        query = query.gte('week_start_date', filters.from_date);
      }
      if (filters?.to_date) {
        query = query.lte('week_end_date', filters.to_date);
      }

      query = query.order('created_at', { ascending: false });

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setEvaluations(data || []);
    } catch (err: any) {
      console.error('Error fetching evaluations:', err);
      setError(err.message || 'Không thể tải danh sách đánh giá');
    } finally {
      setIsLoading(false);
    }
  };

  // Get evaluation with details
  const getEvaluationWithDetails = async (evaluationId: string): Promise<EmployeeEvaluationWithDetails | null> => {
    try {
      const { data: evaluation, error: evalError } = await supabase
        .from('employee_evaluations')
        .select('*')
        .eq('id', evaluationId)
        .single();

      if (evalError) throw evalError;

      // Fetch evaluation details
      const { data: details, error: detailsError } = await supabase
        .from('evaluation_details')
        .select('*')
        .eq('evaluation_id', evaluationId);

      if (detailsError) throw detailsError;

      // Fetch team members separately to avoid nested query issues
      const { data: members, error: membersError } = await supabase
        .from('team_members')
        .select('*')
        .in('id', details?.map(d => d.team_member_id) || []);

      if (membersError) throw membersError;

      // Combine details with members
      const detailsWithMembers = details?.map(detail => ({
        ...detail,
        team_member: members?.find(m => m.id === detail.team_member_id) || null,
      })) || [];

      return {
        ...evaluation,
        evaluation_details: detailsWithMembers,
      };
    } catch (err: any) {
      console.error('Error fetching evaluation details:', err);
      throw new Error(err.message || 'Không thể tải chi tiết đánh giá');
    }
  };

  // Create evaluation
  const createEvaluation = async (formData: EvaluationFormData): Promise<EmployeeEvaluation | null> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      // Get user name from users table (with maybeSingle to handle not found case)
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('user_name')
        .eq('auth_user_id', user.id)
        .maybeSingle();

      // Don't throw error if user not found, just use email
      let evaluatorName = user.email?.split('@')[0] || 'Unknown';
      
      if (!userError && userData?.user_name) {
        evaluatorName = userData.user_name;
      }

      const { data, error: insertError } = await supabase
        .from('employee_evaluations')
        .insert([
          {
            evaluator_user_id: user.id,
            evaluator_name: evaluatorName,
            ...formData,
          },
        ])
        .select()
        .single();

      if (insertError) throw insertError;

      // Refresh list
      await fetchEvaluations();

      return data;
    } catch (err: any) {
      console.error('Error creating evaluation:', err);
      throw new Error(err.message || 'Không thể tạo đánh giá');
    }
  };

  // Update evaluation
  const updateEvaluation = async (id: string, formData: Partial<EvaluationFormData>): Promise<EmployeeEvaluation | null> => {
    try {
      const { data, error: updateError } = await supabase
        .from('employee_evaluations')
        .update(formData)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      // Refresh list
      await fetchEvaluations();

      return data;
    } catch (err: any) {
      console.error('Error updating evaluation:', err);
      throw new Error(err.message || 'Không thể cập nhật đánh giá');
    }
  };

  // Delete evaluation
  const deleteEvaluation = async (id: string): Promise<void> => {
    try {
      const { error: deleteError } = await supabase
        .from('employee_evaluations')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Refresh list
      await fetchEvaluations();
    } catch (err: any) {
      console.error('Error deleting evaluation:', err);
      throw new Error(err.message || 'Không thể xóa đánh giá');
    }
  };

  // Save evaluation detail
  const saveEvaluationDetail = async (
    evaluationId: string,
    detailData: EvaluationDetailFormData
  ): Promise<EvaluationDetail | null> => {
    try {
      // Calculate total score (sum of all scores, not average)
      const scores = [
        detailData.work_quality_score,
        detailData.work_quantity_score,
        detailData.punctuality_score,
        detailData.teamwork_score,
        detailData.attitude_score,
      ].filter(score => score !== undefined) as number[];

      const total_score = scores.length > 0
        ? scores.reduce((sum, score) => sum + score, 0)
        : undefined;

      // Prepare clean data object (only fields that exist in DB)
      const cleanData = {
        work_quality_score: detailData.work_quality_score,
        work_quantity_score: detailData.work_quantity_score,
        punctuality_score: detailData.punctuality_score,
        teamwork_score: detailData.teamwork_score,
        attitude_score: detailData.attitude_score,
        comments: detailData.comments,
        strengths: detailData.strengths,
        improvements: detailData.improvements,
        total_score,
      };

      // Check if detail already exists (maybeSingle returns null if not found)
      const { data: existing, error: checkError } = await supabase
        .from('evaluation_details')
        .select('id')
        .eq('evaluation_id', evaluationId)
        .eq('team_member_id', detailData.team_member_id)
        .maybeSingle();

      if (checkError) throw checkError;

      let result;

      if (existing) {
        // Update existing
        const { data, error } = await supabase
          .from('evaluation_details')
          .update(cleanData)
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('evaluation_details')
          .insert([
            {
              evaluation_id: evaluationId,
              team_member_id: detailData.team_member_id,
              ...cleanData,
            },
          ])
          .select()
          .single();

        if (error) throw error;
        result = data;
      }

      return result;
    } catch (err: any) {
      console.error('Error saving evaluation detail:', err);
      throw new Error(err.message || 'Không thể lưu chi tiết đánh giá');
    }
  };

  // Batch save evaluation details
  const batchSaveEvaluationDetails = async (
    evaluationId: string,
    detailsData: EvaluationDetailFormData[]
  ): Promise<void> => {
    try {
      for (const detail of detailsData) {
        await saveEvaluationDetail(evaluationId, detail);
      }
    } catch (err: any) {
      console.error('Error batch saving evaluation details:', err);
      throw new Error(err.message || 'Không thể lưu chi tiết đánh giá');
    }
  };

  useEffect(() => {
    fetchEvaluations();
  }, [user, filters?.period_id, filters?.status, filters?.from_date, filters?.to_date]);

  return {
    evaluations,
    isLoading,
    error,
    fetchEvaluations,
    getEvaluationWithDetails,
    createEvaluation,
    updateEvaluation,
    deleteEvaluation,
    saveEvaluationDetail,
    batchSaveEvaluationDetails,
  };
};

